import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Save,
  Plus,
  Trash2,
  Edit3,
  X,
  Package,
  Server,
  Zap,
  Users,
  Mail,
  Lock,
  Globe,
  Globe2,
  CheckCircle2,
  Search,
  ArrowUpRight,
  Sparkles,
  Layers,
  HardDrive,
  Copy,
} from "lucide-react";
import api from "../../api";

const CATEGORIES = [
  { id: "all", label: "Alle Pakete", icon: Package },
  { id: "webhosting", label: "Webhosting (NVMe)", icon: Globe },
  { id: "wordpress", label: "WordPress Hosting", icon: Zap },
  { id: "vps", label: "Cloud VPS & VDS", icon: Server },
  { id: "reseller", label: "Reseller Hosting", icon: Users },
  { id: "mail", label: "E-Mail Hosting", icon: Mail },
  { id: "ssl", label: "SSL Zertifikate", icon: Lock },
  { id: "domains", label: "Domain-Preise", icon: Globe2 },
];

const EMPTY_PROD = {
  name: "",
  displayName: "",
  slug: "",
  category: "webhosting",
  unitPrice: 9.90,
  unit: "Monat",
  billingCycles: ["monthly", "yearly"],
  badge: "",
  isHighlight: false,
  shortDescription: "",
  description: "",
  specs: {
    disk: "20 GB NVMe SSD",
    sites: "1 Website",
    ram: "1 GB RAM",
    cpu: "1 vCPU",
    mail: "10 Postfächer",
    traffic: "Unlimitiert",
  },
  featuresText: "20 GB NVMe PCIe 4.0 SSD\n1 Domain inklusive verwaltbar\nUnlimitierter High-Speed Traffic\n10 E-Mail Postfächer (je 5 GB)\n2 MariaDB Datenbanken\nKostenloses Let's Encrypt Auto-SSL\nTägliche Backups (7 Tage)",
  order: 0,
  status: "active",
};

const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-[#E63946] focus:outline-none text-sm bg-white text-[#0f172a] transition";

export default function HostingPackagesAdmin() {
  const [prods, setProds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingProd, setEditingProd] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/products");
      setProds(res.data || []);
    } catch (err) {
      console.error("Fehler beim Laden der Produkte:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Filtered products list
  const filteredProds = useMemo(() => {
    return prods.filter((p) => {
      const matchCat =
        activeCategory === "all" ||
        (p.category && p.category.toLowerCase() === activeCategory.toLowerCase()) ||
        (activeCategory === "webhosting" && (!p.category || p.category === "webspace")) ||
        (activeCategory === "reseller" && (p.category === "reseller" || (p.name || "").toLowerCase().includes("reseller")));
      
      const matchSearch =
        !searchTerm.trim() ||
        (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.displayName && p.displayName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.badge && p.badge.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.shortDescription && p.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchCat && matchSearch;
    });
  }, [prods, activeCategory, searchTerm]);

  // Open modal with prepared fields
  const handleEdit = (p) => {
    const specs = p.specs || {
      disk: "",
      sites: "",
      ram: "",
      cpu: "",
      mail: "",
      traffic: "",
    };
    const featuresList = p.features && Array.isArray(p.features) ? p.features : [];
    setEditingProd({
      ...p,
      displayName: p.displayName || p.name,
      category: p.category || "webhosting",
      specs,
      featuresText: featuresList.join("\n"),
    });
  };

  const handleDuplicate = (p) => {
    const clone = {
      ...p,
      id: undefined,
      _id: undefined,
      name: `${p.name} (Kopie)`,
      displayName: `${p.displayName || p.name} (Kopie)`,
      slug: `${p.slug || "hosting-tarif"}-kopie-${Date.now().toString().slice(-4)}`,
      featuresText: (p.features || []).join("\n"),
    };
    setEditingProd(clone);
  };

  const saveProd = async () => {
    if (!editingProd.name) return;
    setSaving(true);
    try {
      const { id, _id, createdAt, featuresText, ...rest } = editingProd;
      rest.unitPrice = parseFloat(rest.unitPrice) || 0;
      rest.order = Number(rest.order) || 0;

      // Convert featuresText into array
      if (typeof featuresText === "string") {
        rest.features = featuresText
          .split("\n")
          .map((f) => f.trim())
          .filter((f) => f.length > 0);
      }

      if (id) {
        const r = await api.put(`/admin/products/${id}`, rest);
        setProds((arr) => arr.map((x) => (x.id === id ? r.data : x)));
        setSuccessMsg("Paket erfolgreich aktualisiert!");
      } else {
        const r = await api.post("/admin/products", rest);
        setProds((arr) => [...arr, r.data]);
        setSuccessMsg("Neues Paket erfolgreich angelegt!");
      }
      setEditingProd(null);
      setTimeout(() => setSuccessMsg(""), 3500);
    } catch (err) {
      alert("Fehler beim Speichern: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const removeProd = async (id, name) => {
    if (!window.confirm(`Möchten Sie das Paket "${name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/products/${id}`);
      setProds((arr) => arr.filter((x) => x.id !== id));
      setSuccessMsg("Paket gelöscht.");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      alert("Fehler beim Löschen: " + (err.response?.data?.detail || err.message));
    }
  };

  const toggleStatus = async (p) => {
    const newStatus = p.status === "inactive" ? "active" : "inactive";
    try {
      const r = await api.put(`/admin/products/${p.id}`, { ...p, status: newStatus });
      setProds((arr) => arr.map((x) => (x.id === p.id ? r.data : x)));
    } catch (err) {
      alert("Fehler: " + err.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="hosting-packages-admin">
      {/* Top Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap pb-6 border-b border-slate-200">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E63946]/10 text-[#E63946] text-xs font-black uppercase tracking-wider mb-2">
            🇨🇭 Live Webhosting & Server Steuerung
          </div>
          <h1 className="text-3xl font-black text-[#0f172a] tracking-tight">
            Hosting-Pakete & Tarife
          </h1>
          <p className="text-sm text-[#64748b] mt-1 max-w-3xl leading-relaxed">
            Verwalten Sie alle Hosting-, Reseller-, Cloud-Server- und Mail-Pakete in Echtzeit.
            Änderungen an Preisen, Spezifikationen und Features werden unmittelbar auf der öffentlichen Website wirksam.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="/webhosting"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs shadow-sm transition"
          >
            <span>Live-Seite prüfen</span>
            <ArrowUpRight size={14} />
          </a>
          <button
            onClick={() => setEditingProd({ ...EMPTY_PROD })}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E63946] hover:bg-[#c5303d] text-white font-black text-xs shadow-md shadow-red-600/20 transition"
          >
            <Plus size={16} />
            <span>Neues Paket anlegen</span>
          </button>
        </div>
      </div>

      {/* Success Notification */}
      {successMsg && (
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex items-center gap-2 animate-fadeIn">
          <CheckCircle2 size={16} className="text-emerald-600" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Stats Quick Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Gesamtpakete</span>
          <div className="text-2xl font-black text-slate-900 mt-1 font-mono">{prods.length}</div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Aktive Angebote</span>
          <div className="text-2xl font-black text-emerald-600 mt-1 font-mono">
            {prods.filter((p) => p.status !== "inactive").length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Reseller & White-Label</span>
          <div className="text-2xl font-black text-[#FF7A00] mt-1 font-mono">
            {prods.filter((p) => p.category === "reseller" || (p.name || "").toLowerCase().includes("reseller")).length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Kategorien</span>
          <div className="text-2xl font-black text-blue-600 mt-1 font-mono">7 Säulen</div>
        </div>
      </div>

      {/* Filter Tabs & Search */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
            {CATEGORIES.map((cat) => {
              const IconComp = cat.icon;
              const isSelected = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition ${
                    isSelected
                      ? "bg-[#0f172a] text-white shadow-sm"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <IconComp size={14} className={isSelected ? "text-[#E63946]" : "text-slate-400"} />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-64">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Paket suchen..."
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-900 focus:outline-none focus:border-[#E63946]"
            />
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-16 text-center">
            <Loader2 className="animate-spin mx-auto text-[#E63946]" size={32} />
            <p className="text-xs text-slate-500 mt-2 font-medium">Lade Pakete...</p>
          </div>
        ) : filteredProds.length === 0 ? (
          <div className="p-16 text-center text-slate-500">
            <Package size={36} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-bold text-slate-700">Keine Pakete gefunden.</p>
            <p className="text-xs text-slate-400 mt-1">
              Erstellen Sie mit dem Button oben ein neues Hosting-Angebot.
            </p>
          </div>
        ) : (
          <>
            {/* MOBILE CARDS VIEW (Visible only on mobile screens < 768px) */}
            <div className="block md:hidden divide-y divide-slate-100 p-3 space-y-3">
              {filteredProds.map((p) => {
                const isActive = p.status !== "inactive";
                return (
                  <div
                    key={p.id || p._id}
                    className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-extrabold text-slate-900 text-sm flex items-center gap-1.5 flex-wrap">
                          <span>{p.displayName || p.name}</span>
                          {p.badge && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-red-50 text-[#E63946] border border-red-200">
                              {p.badge}
                            </span>
                          )}
                          {p.isHighlight && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-amber-50 text-amber-700 border border-amber-200">
                              Bestseller
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 capitalize">
                          {p.category || "webhosting"}
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="font-mono font-black text-slate-900 text-sm block">
                          CHF {Number(p.unitPrice || p.price || 0).toFixed(2)}
                        </span>
                        <span className="text-[10px] text-slate-400">/{p.unit || "Monat"}</span>
                      </div>
                    </div>

                    {/* Specs summary */}
                    {p.specs && (
                      <div className="flex items-center gap-1.5 flex-wrap text-[10px] text-slate-600 font-mono">
                        {p.specs.disk && (
                          <span className="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200">
                            💾 {p.specs.disk}
                          </span>
                        )}
                        {p.specs.ram && (
                          <span className="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200">
                            ⚡ {p.specs.ram}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                      <button
                        onClick={() => toggleStatus(p)}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider transition ${
                          isActive
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-slate-200 text-slate-600"
                        }`}
                      >
                        {isActive ? "Aktiv" : "Deaktiviert"}
                      </button>

                      <div className="flex items-center gap-1.5">
                        <button
                          title="Duplizieren"
                          onClick={() => handleDuplicate(p)}
                          className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition"
                        >
                          <Copy size={13} />
                        </button>
                        <button
                          title="Bearbeiten"
                          onClick={() => handleEdit(p)}
                          className="px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-[#E63946] text-white text-xs font-bold flex items-center gap-1 transition"
                        >
                          <Edit3 size={13} />
                          <span>Edit</span>
                        </button>
                        <button
                          title="Löschen"
                          onClick={() => removeProd(p.id || p._id, p.name)}
                          className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* DESKTOP TABLE VIEW (Visible on >= 768px screens) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-black uppercase text-slate-500 tracking-wider">
                  <tr>
                    <th className="p-4">Paket & Display Name</th>
                    <th className="p-4">Kategorie</th>
                    <th className="p-4 text-right">Preis (CHF)</th>
                    <th className="p-4">Spezifikationen</th>
                    <th className="p-4 text-center">Status</th>
                    <th className="p-4 text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {filteredProds.map((p) => {
                    const isActive = p.status !== "inactive";
                    return (
                      <tr key={p.id || p._id} className="hover:bg-slate-50/80 transition">
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-slate-900 text-sm">
                              {p.displayName || p.name}
                            </span>
                            {p.badge && (
                              <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-red-50 text-[#E63946] border border-red-200">
                                {p.badge}
                              </span>
                            )}
                            {p.isHighlight && (
                              <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-0.5">
                                <Sparkles size={10} />
                                Bestseller
                              </span>
                            )}
                          </div>
                          {p.shortDescription && (
                            <p className="text-slate-500 text-[11px] mt-0.5 truncate max-w-md">
                              {p.shortDescription}
                            </p>
                          )}
                        </td>

                        <td className="p-4">
                          <span className="px-2.5 py-1 rounded-lg bg-slate-100 font-bold text-slate-700 capitalize text-[11px]">
                            {p.category || "webhosting"}
                          </span>
                        </td>

                        <td className="p-4 text-right font-mono font-black text-slate-900 text-sm">
                          CHF {Number(p.unitPrice || p.price || 0).toFixed(2)}
                          <span className="text-slate-400 font-normal text-[10px] block">/ Monat</span>
                        </td>

                        <td className="p-4 text-[11px] text-slate-600 font-mono">
                          {p.specs ? (
                            <div className="space-y-0.5">
                              <div>💾 {p.specs.disk || "Standard"}</div>
                              <div>⚡ {p.specs.ram || "Shared RAM"}</div>
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>

                        <td className="p-4 text-center">
                          <button
                            onClick={() => toggleStatus(p)}
                            className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider transition ${
                              isActive
                                ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                : "bg-slate-200 text-slate-600 hover:bg-slate-300"
                            }`}
                          >
                            {isActive ? "Aktiv" : "Deaktiviert"}
                          </button>
                        </td>

                        <td className="p-4 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              title="Duplizieren"
                              onClick={() => handleDuplicate(p)}
                              className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition"
                            >
                              <Copy size={13} />
                            </button>
                            <button
                              title="Bearbeiten"
                              onClick={() => handleEdit(p)}
                              className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-[#E63946] hover:text-white text-slate-700 flex items-center justify-center transition"
                            >
                              <Edit3 size={14} />
                            </button>
                            <button
                              title="Löschen"
                              onClick={() => removeProd(p.id || p._id, p.name)}
                              className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Full Modal Editor for Package */}
      {editingProd && (
        <Modal
          title={editingProd.id ? `Paket bearbeiten: ${editingProd.name}` : "Neues Hosting-Paket erstellen"}
          onClose={() => setEditingProd(null)}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Interner Name *">
                <input
                  type="text"
                  required
                  value={editingProd.name}
                  onChange={(e) => setEditingProd({ ...editingProd, name: e.target.value })}
                  className={inp}
                  placeholder="z.B. Linux NVMe Webspace"
                />
              </Field>

              <Field label="Anzeigename (Display Name)">
                <input
                  type="text"
                  value={editingProd.displayName || ""}
                  onChange={(e) => setEditingProd({ ...editingProd, displayName: e.target.value })}
                  className={inp}
                  placeholder="z.B. NVMe Webspace Basic"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Kategorie *">
                <select
                  value={editingProd.category || "webhosting"}
                  onChange={(e) => setEditingProd({ ...editingProd, category: e.target.value })}
                  className={inp}
                >
                  <option value="webhosting">Webhosting (NVMe)</option>
                  <option value="wordpress">WordPress Hosting</option>
                  <option value="vps">Cloud VPS & VDS</option>
                  <option value="reseller">Reseller Hosting (White-Label)</option>
                  <option value="mail">E-Mail Hosting</option>
                  <option value="ssl">SSL Zertifikate</option>
                  <option value="domains">Domain Services</option>
                </select>
              </Field>

              <Field label="Monatspreis (CHF) *">
                <input
                  type="number"
                  step="0.10"
                  required
                  value={editingProd.unitPrice}
                  onChange={(e) => setEditingProd({ ...editingProd, unitPrice: e.target.value })}
                  className={inp}
                />
              </Field>

              <Field label="Badge (z.B. 100% White-Label, BESTSELLER)">
                <input
                  type="text"
                  value={editingProd.badge || ""}
                  onChange={(e) => setEditingProd({ ...editingProd, badge: e.target.value })}
                  className={inp}
                  placeholder="z.B. 100% White-Label"
                />
              </Field>
            </div>

            <Field label="Kurzbeschreibung (Lead Text)">
              <textarea
                rows={2}
                value={editingProd.shortDescription || ""}
                onChange={(e) => setEditingProd({ ...editingProd, shortDescription: e.target.value })}
                className={inp}
                placeholder="z.B. 100% White-Label mit WHM, cPanel und eigener Kundenverwaltung"
              />
            </Field>

            {/* Hardware Telemetry / Specs */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
              <span className="text-xs font-black uppercase text-slate-700 tracking-wider block">
                Hardware-Spezifikationen & Ressourcen
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">Speicherplatz (Disk)</label>
                  <input
                    type="text"
                    value={editingProd.specs?.disk || ""}
                    onChange={(e) =>
                      setEditingProd({
                        ...editingProd,
                        specs: { ...editingProd.specs, disk: e.target.value },
                      })
                    }
                    className={inp}
                    placeholder="250 GB NVMe SSD"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">Websites / Konten</label>
                  <input
                    type="text"
                    value={editingProd.specs?.sites || ""}
                    onChange={(e) =>
                      setEditingProd({
                        ...editingProd,
                        specs: { ...editingProd.specs, sites: e.target.value },
                      })
                    }
                    className={inp}
                    placeholder="25 cPanel Konten"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">RAM-Zuweisung</label>
                  <input
                    type="text"
                    value={editingProd.specs?.ram || ""}
                    onChange={(e) =>
                      setEditingProd({
                        ...editingProd,
                        specs: { ...editingProd.specs, ram: e.target.value },
                      })
                    }
                    className={inp}
                    placeholder="4 GB ECC RAM"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">vCPU Cores</label>
                  <input
                    type="text"
                    value={editingProd.specs?.cpu || ""}
                    onChange={(e) =>
                      setEditingProd({
                        ...editingProd,
                        specs: { ...editingProd.specs, cpu: e.target.value },
                      })
                    }
                    className={inp}
                    placeholder="2 vCPU"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">E-Mail Postfächer</label>
                  <input
                    type="text"
                    value={editingProd.specs?.mail || ""}
                    onChange={(e) =>
                      setEditingProd({
                        ...editingProd,
                        specs: { ...editingProd.specs, mail: e.target.value },
                      })
                    }
                    className={inp}
                    placeholder="Unbegrenzt"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">Traffic / Bandbreite</label>
                  <input
                    type="text"
                    value={editingProd.specs?.traffic || ""}
                    onChange={(e) =>
                      setEditingProd({
                        ...editingProd,
                        specs: { ...editingProd.specs, traffic: e.target.value },
                      })
                    }
                    className={inp}
                    placeholder="Unlimitiert"
                  />
                </div>
              </div>
            </div>

            {/* Features List (1 per line) */}
            <Field label="Enthaltene Leistungen (1 Feature pro Zeile)">
              <textarea
                rows={6}
                value={editingProd.featuresText || ""}
                onChange={(e) => setEditingProd({ ...editingProd, featuresText: e.target.value })}
                className={`${inp} font-mono text-xs`}
                placeholder="250 GB Samsung NVMe PCIe 4.0 Speicher&#10;100% White-Label mit eigenen Nameservern&#10;WHM & cPanel Vollzugriff&#10;CloudLinux CageFS Isolation"
              />
              <span className="text-[11px] text-slate-400 mt-1 block">
                Jede Zeile wird auf der Website als eigener Aufzählungspunkt mit grünem Häkchen dargestellt.
              </span>
            </Field>

            {/* Highlights and Status Toggles */}
            <div className="flex items-center gap-6 pt-2">
              <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                <input
                  type="checkbox"
                  checked={editingProd.isHighlight === true}
                  onChange={(e) => setEditingProd({ ...editingProd, isHighlight: e.target.checked })}
                  className="rounded text-[#E63946] focus:ring-[#E63946]"
                />
                <span>Als Bestseller / Highlight hervorheben</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                <input
                  type="checkbox"
                  checked={editingProd.status !== "inactive"}
                  onChange={(e) =>
                    setEditingProd({
                      ...editingProd,
                      status: e.target.checked ? "active" : "inactive",
                    })
                  }
                  className="rounded text-emerald-600 focus:ring-emerald-600"
                />
                <span>Auf der Website veröffentlichen (Aktiv)</span>
              </label>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setEditingProd(null)}
                className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs"
              >
                Abbrechen
              </button>
              <button
                type="button"
                disabled={saving || !editingProd.name}
                onClick={saveProd}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#E63946] hover:bg-[#c5303d] disabled:opacity-50 text-white font-black text-xs shadow-md shadow-red-600/20"
              >
                {saving ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>Speichern...</span>
                  </>
                ) : (
                  <>
                    <Save size={14} />
                    <span>Änderungen speichern</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-black uppercase tracking-wider text-slate-600 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl sm:rounded-3xl max-w-3xl w-full max-h-[94vh] overflow-y-auto shadow-2xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#0f172a] text-white p-4 sm:p-5 rounded-t-2xl sm:rounded-t-3xl flex items-center justify-between sticky top-0 z-10 border-b border-slate-800">
          <h2 className="text-sm sm:text-base font-extrabold flex items-center gap-2">
            <Server size={18} className="text-[#E63946] shrink-0" />
            <span className="truncate">{title}</span>
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-slate-300 transition shrink-0"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-4 sm:p-6">{children}</div>
      </div>
    </div>
  );
}
